import {Injectable} from "@angular/core";
import {FormFieldGroupConfig} from "../../../model/api/form-field-group-config";
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {OrderCrudComponent} from "../../../crystal-clear/support/order-crud/order-crud.component";
import {Order} from "../../../model/clearing/order";
import {resolveFieldTypes, FieldKind} from "../../../utils/object-reflection";
import {buildFormField} from "../../../utils/form-helper";
import {FieldRecord} from "../../../model/api/field-record";
import {SupportOrderDataLoadingService} from "./support-order-data-loading.service";
import {Account} from "../../../model/clearing/account";
import TYPE = FormFieldConfig.TYPE;
import {TableColumnUtils} from "../../../utils/table-column-utils";
import {TableColumnConfig} from "../../../model/api/table-column-config";

@Injectable({
  providedIn: 'root'
})
export class SupportOrderFormConfigService {

  public static readonly fieldTypes = resolveFieldTypes<Order>({

    // ------------------------------------------------------------------
    // ENUMS
    // ------------------------------------------------------------------
    side: 'enum',
    strategy: 'enum',
    state: 'enum',
    callPut: 'enum',
    openClose: 'enum',
    brokerageRule: 'enum',
    thirdPartySystemFeeStatus: 'enum',

    // ------------------------------------------------------------------
    // NUMBERS
    // ------------------------------------------------------------------
    price: 'number',
    avgPrice: 'number',
    overrideAvgPrice: 'number',
    avgPriceResidual: 'number',
    avgPriceResidualGroup: 'number',
    avgPriceResidualAlloc: 'number',
    avgRndPrice: 'number',
    volume: 'number',
    unmatchedVolume: 'number',
    strike: 'number',

    brokerageFee: 'number',
    overrideBrokerageFee: 'number',
    brokerageRateAmount: 'number',
    overrideBrokerageRateAmount: 'number',

    contractSize: 'number',
    invoiceId: 'number',
    settlementId: 'number',
    obLegRank: 'number',

    // ------------------------------------------------------------------
    // BOOLEANS
    // ------------------------------------------------------------------
    avgPriceManaged: 'boolean',
    isChecked: 'boolean',
    isDisabled: 'boolean',
    giveIn: 'boolean',
    giveUp: 'boolean',
    jobOut: 'boolean',
    forceClear: 'boolean',
    brokerageOverridden: 'boolean',
    alreadyPaidElsewhere: 'boolean',
    unknownAccount: 'boolean',
    manualInvoiceRemoved: 'boolean',

    // ------------------------------------------------------------------
    // DATES (Date)
    // ------------------------------------------------------------------
    businessDate: 'date',
    clearingDate: 'date',
    executionDate: 'date',
    creationDate: 'date',
    modificationDate: 'date',

    // ------------------------------------------------------------------
    // DATETIME (Luxon)
    // ------------------------------------------------------------------
    brokerageDate: 'datetime',

    // ------------------------------------------------------------------
    // OBJECTS
    // ------------------------------------------------------------------
    account: 'object',
    clearingInfo: 'object',

    // ------------------------------------------------------------------
    // ARRAYS
    // ------------------------------------------------------------------
    clearings: 'array'

  });

  public loadConfig(component: OrderCrudComponent): FormFieldGroupConfig[] {

    const record = component.newRecord;
    if (!record) {
      return [];
    }

    const autoFields: FormFieldConfig[] =
      (Object.keys(record) as Array<keyof Order>)
        .filter(field => !this.isExcluded(field))
        .map(field =>
          buildFormField<Order>(field, SupportOrderFormConfigService.fieldTypes[field])
        );

    autoFields.forEach(field => field.options = SupportOrderFormConfigService.buildOptions(field, component.dataLoadingService));

    const accountFields: FormFieldConfig[] =
      (Object.keys(record.account) as Array<keyof Account>)
        .map(field => buildFormField<Account>(field, SupportOrderFormConfigService.fieldTypes[field]));
    const accountFieldTableConfig: TableColumnConfig[] = (Object.keys(component.newRecord.account ?? {}) as Array<keyof Account>)
      .map(field => {
        return TableColumnUtils.buildColumn<Account>(
          field,
          SupportOrderFormConfigService.fieldTypes[field],
        );
      });

    return [
      {
        group: 'account',
        fields: [
          {
            field: 'account',
            column: 12,
            type: TYPE.SINGLERICHFIELD,
            richFieldConfig: {
              fieldId: 'name',
              fieldName: 'name',
              tableConfigs: accountFieldTableConfig,
              fields: [
                {
                  group: '',
                  fields: accountFields
                }]
            }
          }
        ]
      }
      , {
        group: 'fields',
        fields: autoFields
      }
    ];
  }

  private isExcluded(field: keyof Order): boolean {
    return [
      'clearings',
      'clearingInfo',
      'account'
    ].includes(field as string);
  }

  // ---------------------------------------------------------------------
  // Order-specific options
  // ---------------------------------------------------------------------

  public static buildOptions(
    fieldConf: FormFieldConfig,
    dataLoadingService: SupportOrderDataLoadingService
  ): () => FieldRecord<any>[] {

    switch (fieldConf.field) {
      case 'callPut':
        return () => dataLoadingService.callPuts;
      case 'strategy':
        return () => dataLoadingService.strategies;
      case 'agressorInd':
        return () => dataLoadingService.agressors;
      case 'side':
        return () => dataLoadingService.sides;
      case 'state':
        return () => dataLoadingService.orderStates;
      case 'openClose':
        return () => dataLoadingService.openCloses;
      case 'brokerageRule':
        return () => dataLoadingService.brokerageRules;
      case 'thirdPartySystemFeeStatus':
        return () => dataLoadingService.thirdPartySystemFeeStatus;
      default:
        return () => undefined;
    }
  }
}
